import { useState, ReactNode, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Language, Translations } from '@/types/language-types';
import { LanguageContext } from '@/lib/language-context';
import { isSupportedLanguage, DEFAULT_LANGUAGE } from '@/config/languages';

interface LanguageProviderProps {
  children: ReactNode;
  initialLanguage?: Language;
  initialTranslations?: Translations | null;
}

export const LanguageProvider = ({ 
  children, 
  initialLanguage = 'en', 
  initialTranslations = null 
}: LanguageProviderProps) => {
  const [currentLanguage, setCurrentLanguage] = useState<Language>(initialLanguage);
  const [translations, setTranslations] = useState<Translations | null>(initialTranslations);
  const location = useLocation();
  const navigate = useNavigate();

  // Determine language from URL
  useEffect(() => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const firstSegment = pathSegments[0];
    
    if (firstSegment && isSupportedLanguage(firstSegment)) {
       if (currentLanguage !== firstSegment) {
         setCurrentLanguage(firstSegment as Language);
       }
    } else {
       if (currentLanguage !== DEFAULT_LANGUAGE) {
         setCurrentLanguage(DEFAULT_LANGUAGE);
       }
    }
  }, [location.pathname, currentLanguage]);

  const loadTranslations = async (lang: Language) => {
    try {
      console.log('Loading translations for language:', lang);
      
      let translationModule;
      switch (lang) {
        case 'hi':
          translationModule = await import('../translations/hi.json');
          break;
        case 'mr':
          translationModule = await import('../translations/mr.json');
          break;
        case 'gu':
          translationModule = await import('../translations/gu.json');
          break;
        case 'ta':
          translationModule = await import('../translations/ta.json');
          break;
        case 'te':
          translationModule = await import('../translations/te.json');
          break;
        case 'kn':
          translationModule = await import('../translations/kn.json');
          break;
        case 'bn':
          translationModule = await import('../translations/bn.json');
          break;
        default:
          translationModule = await import('../translations/en.json');
      }
      
      return translationModule.default || translationModule;
    } catch (error) {
      console.warn(`Translation file not found for language: ${lang}, falling back to English`);
      const fallbackModule = await import('../translations/en.json');
      return fallbackModule.default || fallbackModule;
    }
  };

  // Load translations when language changes
  useEffect(() => {
    const loadLanguageTranslations = async () => {
      console.log('Loading translations for language:', currentLanguage);
      const newTranslations = await loadTranslations(currentLanguage);
      setTranslations(newTranslations);
      console.log('Loaded translations:', newTranslations);
    };

    loadLanguageTranslations();
  }, [currentLanguage]);

  const setLanguage = (language: Language) => {
    console.log('Language changing to:', language);
    
    // Update URL instead of just state
    const currentPath = location.pathname;
    const pathSegments = currentPath.split('/').filter(Boolean);
    const firstSegment = pathSegments[0];
    
    let newPath = '';
    
    // Check if current URL starts with a supported language
    if (firstSegment && isSupportedLanguage(firstSegment)) {
       // Replace existing language
       if (language === DEFAULT_LANGUAGE) {
          // Remove language prefix (e.g. /hi/about -> /about)
          newPath = '/' + pathSegments.slice(1).join('/');
       } else {
          // Switch language prefix (e.g. /hi/about -> /mr/about)
          pathSegments[0] = language;
          newPath = '/' + pathSegments.join('/');
       }
    } else {
       // No language prefix currently (English or root)
       if (language !== DEFAULT_LANGUAGE) {
          // Add prefix (e.g. /about -> /hi/about)
          newPath = `/${language}${currentPath === '/' ? '' : currentPath}`;
       } else {
          // Staying in English, do nothing
          return;
       }
    }
    
    // If path is empty string (root), make it /
    if (newPath === '') newPath = '/';
    
    // Store intent to scroll to biodata creator section
    try {
      sessionStorage.setItem('shouldScrollToCreator', 'true');
    } catch (e) {
      console.warn('Failed to save scroll intent to sessionStorage', e);
    }
    
    // Preserve query params and hash
    const search = location.search;
    const hash = location.hash;
    const finalPath = `${newPath}${search}${hash}`;

    navigate(finalPath);
    // State update will happen via useEffect listening to location change
  };

  const t = (key: string): string => {
    try {
      if (!translations) {
        console.warn('Translations not loaded yet');
        return key;
      }
      
      // Handle nested keys like "ui.save_continue"
      const keys = key.split('.');
      let value: string | Translations = translations;
      
      for (const k of keys) {
        if (value && typeof value === 'object' && k in value) {
          value = value[k];
        } else {
          console.warn(`Translation not found for key: ${key} in language: ${currentLanguage}`);
          return key; // Return key if translation not found
        }
      }
      
      return typeof value === 'string' ? value : key;
    } catch (error) {
      console.warn(`Translation error for key: ${key} in language: ${currentLanguage}`, error);
      return key;
    }
  };

  const getFieldTranslation = (section: string, field: string, type: 'label' | 'placeholder' = 'label'): string => {
    try {
      if (!translations) {
        console.warn('Translations not loaded yet for field translation');
        return field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      }

      const fieldData = translations.sections?.[section]?.fields?.[field];
      
      if (fieldData && fieldData[type]) {
        console.log(`Found translation for ${section}.${field}.${type}:`, fieldData[type]);
        return fieldData[type];
      }
      
      // Fallback to formatted field name if translation not found
      const fallback = field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      console.warn(`Field translation not found for: ${section}.${field}.${type} in language: ${currentLanguage}, using fallback: ${fallback}`);
      return fallback;
    } catch (error) {
      console.warn(`Field translation error for: ${section}.${field}.${type} in language: ${currentLanguage}`, error);
      return field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
  };

  const getSectionTitle = (section: string): string => {
    try {
      if (!translations) {
        console.warn('Translations not loaded yet for section title');
        return section.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      }

      const title = translations.sections?.[section]?.title;
      
      if (title) {
        console.log(`Found section title for ${section}:`, title);
        return title;
      }
      
      // Fallback to formatted section name
      const fallback = section.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      console.warn(`Section title not found for: ${section} in language: ${currentLanguage}, using fallback: ${fallback}`);
      return fallback;
    } catch (error) {
      console.warn(`Section title error for: ${section} in language: ${currentLanguage}`, error);
      return section.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
  };

  return (
    <LanguageContext.Provider value={{ currentLanguage, setLanguage, t, getFieldTranslation, getSectionTitle }}>
      {children}
    </LanguageContext.Provider>
  );
};
